import { JSX } from "./index";

export type TagComponents = { [tag: string]: (children: JSX.Child[]) => JSX.Element<JSX.Type, JSX.Props> };

const TAG = /<(\/?)([A-Za-z][A-Za-z0-9]*)>/g;

interface Frame {
    tag: string;
    children: JSX.Child[];
}

function append(children: JSX.Child[], child: JSX.Child) {
    const last = children.length - 1;
    if (typeof child === "string" && typeof children[last] === "string") {
        children[last] += child;
    } else if (child !== "") {
        children.push(child);
    }
}

/**
 * Turns a ContentReef string such as "Read the <link>notice</link>." into jsx-act children, building
 * each known tag with its component. Text always becomes text nodes; a tag that is unknown, unclosed or
 * closed out of order stays literal text, so a translation error cannot inject markup.
 */
export function renderTaggedString(text: string, components: TagComponents): JSX.Child[] {
    const stack: Frame[] = [{ tag: "", children: [] }];
    let position = 0;
    let match: RegExpExecArray | null;
    TAG.lastIndex = 0;
    while ((match = TAG.exec(text)) !== null) {
        const [token, closing, tag] = match;
        const top = stack[stack.length - 1];
        append(top.children, text.slice(position, match.index));
        position = match.index + token.length;
        if (!Object.prototype.hasOwnProperty.call(components, tag)) {
            append(top.children, token);
        } else if (!closing) {
            stack.push({ tag, children: [] });
        } else if (stack.length > 1 && top.tag === tag) {
            stack.pop();
            append(stack[stack.length - 1].children, components[tag](top.children));
        } else {
            append(top.children, token);
        }
    }
    append(stack[stack.length - 1].children, text.slice(position));
    while (stack.length > 1) {
        const unclosed = stack.pop()!;
        const parent = stack[stack.length - 1].children;
        append(parent, `<${unclosed.tag}>`);
        unclosed.children.forEach(child => append(parent, child));
    }
    return stack[0].children;
}
